import React, { useState, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import InputText from "../../components/ui/inputText/InputText";
import Select from "../../components/ui/select/Select";
import { useLoaderData } from "react-router-dom";
import { BusinessProfile, UserProfile } from "../../types/profile";
import { RolesContext } from "../../context/rolesContext";
import { IRolesContext } from "../../types/roles";
import { ProfileErrorObj, validateProfileData } from "../../utils/helpers/validations/validateProfileData";
import { canPass } from "../../utils/helpers/validations/canPass";
import { profileService } from "../../api/profileService/profileService";
import { toast } from "react-toastify";
import Spinner from "../../components/ui/loaders/spinner/Spinner";
import { BusinessProfileErrorObj, validateBusinessProfileData } from "../../utils/helpers/validations/validateBusinessProfileData";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomDatePickerInput from "../../components/customDatePickerInput/CustomDatePickerInput";
import i18n from "../../i18n";
import  it  from "date-fns/locale/it"
import  enUS  from "date-fns/locale/en-US"
import Button from "../../components/ui/button/Button";
import CheckBox from "../../components/ui/checkbox/CheckBox";

const Profile: React.FC = () => {
    const { t } = useTranslation();
    const { roles } = useContext(RolesContext) as IRolesContext;
    const {
        profileApi,
        businessProfileApi
    } = useLoaderData() as { profileApi: null | UserProfile, businessProfileApi: null | BusinessProfile };

    // Errors for Profile
    const [errorState, setErrorState] = useState<ProfileErrorObj>({
        phone: {
            error: false,
            message: ""
        },
        role: {
            error: false,
            message: ""
        }
    });

    // Errors for Business Profile
    const [errorStateBusinessProfile, setErrorStateBusinessProfile] = useState<BusinessProfileErrorObj>({
        name: {
            error: false,
            message: ""
        },
        vatnr: {
            error: false,
            message: ""
        }
    });

    const [rolesOptions, setRolesOptions] = useState<{ value: number, label: string }[]>([]);
    useEffect(() => {
        if (!!roles) {
            setRolesOptions([...roles].map((role) => {
                return {
                    label: role.value,
                    value: role.id
                }
            }));
        }
    }, [roles]);

    const [updatedBusinessProfile, setUpdatedBusinessProfile] = useState<BusinessProfile | null>(businessProfileApi || null)
    const [businessProfile, setBusinessProfile] = useState<BusinessProfile | null>(businessProfileApi || null)

    // Modifying Profile
    const [isModifyMode, setIsModifyMode] = useState(false);
    const [loading, setLoading] = useState(false);

    // Modifying Business Profile
    const [isModifyModeBusiness, setIsModifyModeBusiness] = useState(false);
    const [loadingBusiness, setLoadingBusiness] = useState(false);

    const [initialProfileApi, setInitialProfileApi] = useState<UserProfile | null>(profileApi || null);

    const [phone, setPhone] = useState(!!profileApi ? profileApi.phone : '');
    const [role, setRole] = useState(!!profileApi ? profileApi.role.id : 0);
    const [privacyConsent, setPrivacyConsent] = useState(!!profileApi ? profileApi.privacyConsent : false);
    const [marketingConsent, setMarketingConsent] = useState(!!profileApi ? profileApi.marketingConsent : false);
    const [collaborationConsent, setCollaborationConsent] = useState(!!profileApi ? profileApi.collaborationConsent : false);

    const [variationEffectiveDate, setVariationEffectiveDate] = useState<Date | null>(null);
    const [errorVariationEffectiveDate, setErrorVariationEffectiveDate] = useState({ error: false, message: "" });


    const getInitials = useMemo(() => {
        if (profileApi) {
            return profileApi.name[0] + profileApi.surname[0];
        }

        return '';
    }, [profileApi]);

    const handleProfileRequest = async () => {
        if (!!initialProfileApi && (phone !== initialProfileApi.phone || role !== initialProfileApi.role.id || marketingConsent !== initialProfileApi.marketingConsent || privacyConsent !== initialProfileApi.privacyConsent || collaborationConsent !== initialProfileApi.collaborationConsent)) {
            const errorObj = validateProfileData({
                phone: phone,
                role: role
            });

            const canGo = canPass(errorObj);
            if (canGo === true) {
                setLoading(true);
                const modifyProfileResponse = await profileService.modifyProfile({
                    phone: parseInt(phone).toString(),
                    role: role,
                    privacyConsent: privacyConsent,
                    marketingConsent: marketingConsent,
                    collaborationConsent: collaborationConsent
                });

                if (modifyProfileResponse.success) {
                    const getUsersResponse = await profileService.getProfile();
                    if (getUsersResponse.success) {
                        setPhone(getUsersResponse.responseBody.phone);
                        setRole(getUsersResponse.responseBody.role.id);
                        setPrivacyConsent(getUsersResponse.responseBody.privacyConsent);
                        setMarketingConsent(getUsersResponse.responseBody.marketingConsent);
                        setCollaborationConsent(getUsersResponse.responseBody.collaborationConsent)

                        setInitialProfileApi({ ...getUsersResponse.responseBody });
                    } else {
                        window.location.reload();
                    }
                } else {
                    toast.error(t('errors.errorModifyingProfile'))
                }

                setLoading(false);
                setIsModifyMode(false);
            } else {
                setErrorState(errorObj);
            }
        } else {
            setIsModifyMode(false);
        }
    }

    const resetError = (errorName: "phone" | "role") => () => {
        setErrorState({
            ...errorState,
            [errorName]: {
                error: false,
                message: ''
            }
        })
    }

    const resetErrorBusiness = (errorName: "name" | "vatnr") => () => {
        setErrorStateBusinessProfile({
            ...errorStateBusinessProfile,
            [errorName]: {
                error: false,
                message: ''
            }
        })
    }

    const BUSINESS_PROFILE_MANDATORY_FIELDS = ["name", "city", "line", "zipcode", "vatnr", "taxcode", "email"] as const;
    const BUSINESS_PROFILE_EDITABLE_FIELDS = [...BUSINESS_PROFILE_MANDATORY_FIELDS, "selfacceptance"] as const;

    const onChangeBusinessProfile = (e: string | boolean, stateName: any) => {
        if (!!businessProfile && BUSINESS_PROFILE_EDITABLE_FIELDS.includes(stateName)) {
            setBusinessProfile({
                ...businessProfile,
                [stateName]: e
            });
        }
    }

    const checkHasChangedBusinessProfile = () => {
        return BUSINESS_PROFILE_EDITABLE_FIELDS.some(field => updatedBusinessProfile?.[field] !== businessProfile?.[field]);
    }

    const checkMandatoryFields = () => {
        return BUSINESS_PROFILE_MANDATORY_FIELDS.some(field => updatedBusinessProfile?.[field] !== businessProfile?.[field]);
    }

    // Formatta la data per il salvataggio
    const formattedVariationEffectiveDate = useMemo(
        () =>
            variationEffectiveDate
                ? ("0" + variationEffectiveDate.getDate()).slice(-2) +
                "/" +
                ("0" + (variationEffectiveDate.getMonth() + 1)).slice(-2) +
                "/" +
                variationEffectiveDate.getFullYear()
                : null,
        [variationEffectiveDate]
    );

    const requestChangeBusinessProfile = async () => {
        if (!!profileApi && !!businessProfile && checkHasChangedBusinessProfile() && profileApi.isAccountAdministrator === true) {
            const trimAndHandleNull = (value: string | null) => (value !== null ? value.trim() : null);
            const trimAndHandleEmptyString = (value: string | null) => (value !== null ? value.trim() : '');

            const formattedProfileData = {
                city: businessProfile.city.trim(),
                line: businessProfile.line.trim(),
                name: businessProfile.name.trim(),
                vatnr: trimAndHandleNull(businessProfile.vatnr),
                taxcode: trimAndHandleNull(businessProfile.taxcode),
                zipcode: businessProfile.zipcode.trim(),
                email: trimAndHandleEmptyString(businessProfile.email),
                variationeffectivedate: formattedVariationEffectiveDate,
                modifyselfacceptance: businessProfile?.selfacceptance !== updatedBusinessProfile?.selfacceptance,
            };

            // Validate profile data
            const errorObj = validateBusinessProfileData(formattedProfileData);

            // Check if variationEffectiveDate is empty
            if (!variationEffectiveDate && checkMandatoryFields()) {
                setErrorVariationEffectiveDate({ error: true, message: "*" });
                return;
            }

            // Proceed if there are no validation errors
            if (canPass(errorObj)) {
                setLoadingBusiness(true);
                try {
                    const request = await profileService.modifyBusinessProfile(formattedProfileData);

                    if (request.success) {
                        const getBusinessProfileResponse = await profileService.getBusinessProfile();
                        if (getBusinessProfileResponse.success) {
                            setBusinessProfile(getBusinessProfileResponse.responseBody);
                            setUpdatedBusinessProfile(getBusinessProfileResponse.responseBody);
                            setIsModifyModeBusiness(false);
                        } else {
                            window.location.reload(); // Reload if unable to fetch updated profile
                        }
                    } else {
                        toast.error(t('errors.errorModifyingBusinessProfile'));
                    }
                } catch (error) {
                    console.error('Error modifying business profile:', error);
                    toast.error(t('errors.unexpectedError'));
                } finally {
                    setLoadingBusiness(false);
                }
            } else {
                setErrorStateBusinessProfile(errorObj);
            }

        } else {
            setIsModifyModeBusiness(false);
        }
    }

    const getLocaleForDatePicker = () => {
        switch (i18n.language) {
            case 'it':
                registerLocale("it", it);
                return 'it'
            case 'en':
            default:
                registerLocale("en", enUS);
                return 'en'
        }
    };

    return (
        <div className="p-6 md:p-8">
            <div className="md:flex items-start mb-6 md:mb-[30px]">
                <h1 className="mb-4 md:mb-0 text-[32px] font-bold leading-[37.5px]">
                    {t('screens.profile.heading')}
                </h1>
            </div>

            <div className="mb-12">
                <h2 className="select-none font-bold text-2xl leading-[29px] pb-2 border-b mb-6">{t('screens.profile.userProfile')}</h2>
                <div className="shadow-card rounded-[4px] bg-lightGrey p-[20px]">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between">
                        <div className="flex items-center mb-3 lg:mb-0">
                            <div className="mr-[20px] h-[70px] w-[70px] font-semibold select-none rounded-full bg-white text-black text-[30px] leading-[39px] uppercase flex items-center justify-center">
                                {getInitials}
                            </div>
                            <div className="text-2xl leading-[29px] flex items-center lg:block">
                                <div className="mr-1 lg:mr-0">{profileApi?.name}</div>
                                <div className="font-bold">{profileApi?.surname}</div>
                            </div>
                        </div>

                        <div className="mb-3 lg:mb-0">
                            <div>
                                <div className="font-semibold text-sm text-red uppercase leading-[17px]">{t('screens.profile.email')}</div>
                                <div className="text-lg leading-[22px]">{profileApi?.email}</div>
                            </div>
                        </div>

                        <div className="mb-3 lg:mb-0">
                            {
                                isModifyMode === false &&
                                <div>
                                    <div className="font-semibold text-sm text-red uppercase leading-[17px]">{t('screens.profile.phone')}</div>
                                    <div className="text-lg leading-[22px]">{phone}</div>
                                </div>
                            }

                            {
                                isModifyMode === true &&
                                <InputText
                                    label={t('screens.profile.phone')}
                                    name="phone"
                                    placeholder={t('screens.profile.phone') as string}
                                    value={phone}
                                    callbackOnChange={(e) => setPhone(e)}
                                    hasError={errorState.phone.error}
                                    errorMessage={errorState.phone.message}
                                    callbackOnFocus={resetError('phone')}
                                />
                            }
                        </div>

                        <div className="mb-3 lg:mb-0">
                            {
                                isModifyMode === false &&
                                <div>
                                    <div className="font-semibold text-sm text-red uppercase leading-[17px]">{t('screens.profile.role')}</div>
                                    <div className="text-lg leading-[22px]">{rolesOptions.find(o => o.value == role)?.label}</div>
                                </div>
                            }

                            {
                                isModifyMode === true &&
                                <Select
                                    callbackOnChange={(e) => setRole(e.target.value)}
                                    label={t('screens.profile.role')}
                                    name="role"
                                    value={role}
                                    placeholder={t('screens.profile.role')}
                                    options={rolesOptions}
                                    hasError={errorState.role.error}
                                    errorMessage={errorState.role.message}
                                    callbackOnFocus={resetError('role')}
                                    disabled={!(!!profileApi && profileApi?.isAccountAdministrator === true)}
                                />
                            }
                        </div>

                        {
                            isModifyMode === false &&
                            <div onClick={() => (setIsModifyMode(true))} className="cursor-pointer h-9 w-9 flex items-center justify-center bg-red rounded-[8px]">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18.988 2.01196L21.988 5.01196L19.701 7.29996L16.701 4.29996L18.988 2.01196ZM8 16H11L18.287 8.71296L15.287 5.71296L8 13V16Z" fill="white" />
                                    <path d="M19 19H8.158C8.132 19 8.105 19.01 8.079 19.01C8.046 19.01 8.013 19.001 7.979 19H5V5H11.847L13.847 3H5C3.897 3 3 3.896 3 5V19C3 20.104 3.897 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V10.332L19 12.332V19Z" fill="white" />
                                </svg>
                            </div>
                        }

                        {
                            isModifyMode === true &&
                            <>
                                {
                                    loading === false &&

                                    <div className="shrink-0 ml-1 mt-4 px-3 text-right">
                                        <Button
                                            callback={handleProfileRequest}
                                        >
                                            {t('common.sidebars.saveButtonText')}
                                        </Button>
                                    </div>
                                }
                                {
                                    loading === true &&
                                    <div className="h-9 w-9 flex items-center justify-center bg-red rounded-[8px]">
                                        <Spinner color="#FFFFFF" />
                                    </div>
                                }
                            </>
                        }
                    </div>
                    <div className="mt-4">
                        <CheckBox
                            id="privacy-checkbox"
                            checked={privacyConsent}
                            onChange={(value) => setPrivacyConsent(value)}
                            label={t('screens.profile.privacyCheckboxLabel')}
                            readOnly={!isModifyMode}
                            size="0.75rem"
                        />

                        <CheckBox
                            id="marketing-checkbox"
                            checked={marketingConsent}
                            onChange={(value) => setMarketingConsent(value)}
                            label={t('screens.profile.marketingCheckboxLabel')}
                            readOnly={!isModifyMode}
                            size="0.75rem"
                        />

                        <CheckBox
                            id="collaboration-checkbox"
                            checked={collaborationConsent}
                            onChange={(value) => setCollaborationConsent(value)}
                            label={t('screens.profile.collaborationCheckboxLabel')}
                            readOnly={!isModifyMode}
                            size="0.75rem"
                        />
                    </div>
                </div>
            </div>

            {
                !!businessProfile &&
                <div>
                    <h2 className="select-none font-bold text-2xl leading-[29px] pb-2 border-b mb-6">{t('screens.profile.businessProfile')}</h2>

                    <div className="grid grid-cols-12 gap-6">
                        <div
                            className={"col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 md:flex md:flex-col md:justify-between rounded-[8px] shadow-card select-none p-4 bg-lightGrey"}
                        >
                            <div>
                                <div className="mb-3">
                                    {
                                        !isModifyModeBusiness &&
                                        <div className="text-2xl leading-[29px]">
                                            {businessProfile.name}
                                        </div>
                                    }
                                    {
                                        isModifyModeBusiness &&
                                        <InputText
                                            label={t('screens.profile.businessName')}
                                            name="business_name"
                                            placeholder={t('screens.profile.businessName') as string}
                                            value={businessProfile.name}
                                            callbackOnChange={(e) => onChangeBusinessProfile(e, "name")}
                                            hasError={errorStateBusinessProfile.name.error}
                                            errorMessage={errorStateBusinessProfile.name.message}
                                            callbackOnFocus={resetErrorBusiness('name')}
                                        />
                                    }
                                </div>

                                <div className="mb-3">
                                    {
                                        isModifyModeBusiness === false &&
                                        <>
                                            <div className="uppercase text-red font-semibold text-sm leading-[17px]">{t('screens.profile.city')}</div>
                                            <div className="text-lg leading-[22px]">{businessProfile.city}</div>
                                        </>
                                    }

                                    {
                                        isModifyModeBusiness === true &&
                                        <InputText
                                            label={t('screens.profile.city')}
                                            name="city"
                                            placeholder={t('screens.profile.city') as string}
                                            value={businessProfile.city}
                                            callbackOnChange={(e) => onChangeBusinessProfile(e, "city")}
                                        />
                                    }
                                </div>

                                <div className="mb-3">
                                    {
                                        isModifyModeBusiness === false &&
                                        <>
                                            <div className="uppercase text-red font-semibold text-sm leading-[17px]">{t('screens.profile.address')}</div>
                                            <div className="text-lg leading-[22px]">{businessProfile.line}</div>
                                        </>
                                    }
                                    {
                                        isModifyModeBusiness === true &&
                                        <InputText
                                            label={t('screens.profile.address')}
                                            name="address"
                                            placeholder={t('screens.profile.address') as string}
                                            value={businessProfile.line}
                                            callbackOnChange={(e) => onChangeBusinessProfile(e, "line")}
                                        />
                                    }
                                </div>

                                <div className="mb-3">
                                    {
                                        isModifyModeBusiness === false &&
                                        <>
                                            <div className="uppercase text-red font-semibold text-sm leading-[17px]">{t('screens.profile.zipCode')}</div>
                                            <div className="text-lg leading-[22px]">{businessProfile.zipcode}</div>
                                        </>
                                    }
                                    {
                                        isModifyModeBusiness === true &&
                                        <InputText
                                            label={t('screens.profile.zipCode')}
                                            name="zipCode"
                                            placeholder={t('screens.profile.zipCode') as string}
                                            value={businessProfile.zipcode}
                                            callbackOnChange={(e) => onChangeBusinessProfile(e, "zipcode")}
                                        />
                                    }
                                </div>

                                {
                                    businessProfile.vatnr !== null &&
                                    <div className="mb-3">
                                        {
                                            isModifyModeBusiness === false &&
                                            <>
                                                <div className="uppercase text-red font-semibold text-sm leading-[17px]">{t('screens.profile.vat')}</div>
                                                <div className="text-lg leading-[22px]">{businessProfile.vatnr}</div>
                                            </>
                                        }
                                        {
                                            isModifyModeBusiness === true &&
                                            <InputText
                                                label={t('screens.profile.vat')}
                                                name="vat"
                                                placeholder={t('screens.profile.vat') as string}
                                                value={businessProfile.vatnr}
                                                callbackOnChange={(e) => onChangeBusinessProfile(e, "vatnr")}
                                                hasError={errorStateBusinessProfile.vatnr.error}
                                                errorMessage={errorStateBusinessProfile.vatnr.message}
                                                callbackOnFocus={resetErrorBusiness('vatnr')}
                                            />
                                        }
                                    </div>
                                }

                                {
                                    businessProfile.taxcode !== null &&
                                    <div className="mb-3">
                                        {
                                            isModifyModeBusiness === false &&
                                            <>
                                                <div className="uppercase text-red font-semibold text-sm leading-[17px]">{t('screens.profile.taxcode')}</div>
                                                <div className="text-lg leading-[22px]">{businessProfile.taxcode}</div>
                                            </>
                                        }
                                        {
                                            isModifyModeBusiness === true &&
                                            <InputText
                                                label={t('screens.profile.taxcode')}
                                                name="taxcode"
                                                placeholder={t('screens.profile.taxcode') as string}
                                                value={businessProfile.taxcode}
                                                callbackOnChange={(e) => onChangeBusinessProfile(e, "taxcode")}
                                            />
                                        }
                                    </div>
                                }

                                {
                                    businessProfile.isocode === 'IT' &&
                                    <div className="mb-3 overflow-hidden">
                                        {
                                            isModifyModeBusiness === false &&
                                            <>
                                                <div className="uppercase text-red font-semibold text-sm leading-[17px]">{t('screens.profile.pec')}</div>
                                                <div className="text-lg leading-[22px] overflow-hidden text-ellipsis">{!!businessProfile.email ? businessProfile.email : '-'}</div>
                                            </>
                                        }
                                        {
                                            isModifyModeBusiness &&
                                            <InputText
                                                label={t('screens.profile.pec')}
                                                name="business_email"
                                                placeholder={t('screens.profile.pec') as string}
                                                value={!!businessProfile.email ? businessProfile.email : ''}
                                                callbackOnChange={(e) => onChangeBusinessProfile(e, "email")}
                                            />
                                        }
                                    </div>
                                }

                                {
                                    !!businessProfile.status &&
                                    <div className="mb-3">
                                        <div className="uppercase text-red font-semibold text-sm leading-[17px]">{t('screens.profile.status')}</div>
                                        <div className="text-lg leading-[22px]">{businessProfile.status?.value}</div>
                                    </div>
                                }

                                <div className="mb-3">
                                    {isModifyModeBusiness && (
                                        <div>
                                            <label className="uppercase text-red font-semibold text-sm leading-[17px]">
                                                {t('screens.profile.variationEffectiveDate')}
                                                {errorVariationEffectiveDate.error && (
                                                    <span className="text-red text-sm">
                                                        {' *'}
                                                    </span>
                                                )}
                                            </label>
                                            <DatePicker
                                                locale={getLocaleForDatePicker()}
                                                wrapperClassName="w-full"
                                                className="w-full"
                                                selected={variationEffectiveDate}
                                                onChange={(date: Date | null) => setVariationEffectiveDate(date)}
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText={t('screens.profile.deteFormat') as string}
                                                customInput={<CustomDatePickerInput error={errorVariationEffectiveDate.error} />}
                                                popperPlacement="top-end"
                                                renderCustomHeader={({
                                                    date,
                                                    decreaseMonth,
                                                    increaseMonth,
                                                    prevMonthButtonDisabled,
                                                    nextMonthButtonDisabled,
                                                }) => (
                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 10px" }}>
                                                        <button
                                                            onClick={decreaseMonth}
                                                            disabled={prevMonthButtonDisabled}
                                                            style={{ background: "none", border: "none", cursor: "pointer" }}
                                                        >
                                                            {"<"}
                                                        </button>
                                                        <span style={{ textTransform: "capitalize", fontWeight: 'bold' }}>
                                                            {date.toLocaleDateString(i18n.language, { month: "long", year: "numeric" })}
                                                        </span>
                                                        <button
                                                            onClick={increaseMonth}
                                                            disabled={nextMonthButtonDisabled}
                                                            style={{ background: "none", border: "none", cursor: "pointer" }}
                                                        >
                                                            {">"}
                                                        </button>
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    )}
                                </div>
                                {
                                businessProfile.isagency===true && 
                                businessProfile.selfacceptance !== null &&
                                    <div className="mb-3">
                                        <div className="uppercase text-red font-semibold text-sm leading-[17px]">{t('screens.profile.selfacceptance')}</div>
                                        <CheckBox
                                            id="selfacceptance-checkbox"
                                            checked={businessProfile.selfacceptance}
                                            onChange={(value) => onChangeBusinessProfile(value, "selfacceptance")}
                                            readOnly={!isModifyModeBusiness}
                                            size="1.5rem"
                                            
                                            
                                        />
                                    </div>
                                }

                            </div>

                            {
                                !!profileApi && (profileApi?.isAccountAdministrator === true) && businessProfile.status === null &&
                                <div className="pt-2 border-t">
                                    {
                                        isModifyModeBusiness === false &&
                                        <div onClick={() => setIsModifyModeBusiness(true)} className="cursor-pointer h-9 w-9 flex items-center justify-center bg-red rounded-[8px]">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18.988 2.01196L21.988 5.01196L19.701 7.29996L16.701 4.29996L18.988 2.01196ZM8 16H11L18.287 8.71296L15.287 5.71296L8 13V16Z" fill="white" />
                                                <path d="M19 19H8.158C8.132 19 8.105 19.01 8.079 19.01C8.046 19.01 8.013 19.001 7.979 19H5V5H11.847L13.847 3H5C3.897 3 3 3.896 3 5V19C3 20.104 3.897 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V10.332L19 12.332V19Z" fill="white" />
                                            </svg>
                                        </div>
                                    }

                                    {
                                        isModifyModeBusiness === true &&
                                        <>
                                            {
                                                loadingBusiness === false &&

                                                <div className="shrink-0 ml-1 mt-4 px-3 text-right">
                                                    <Button
                                                        callback={requestChangeBusinessProfile}
                                                    >
                                                        {t('common.sidebars.saveButtonText')}
                                                    </Button>
                                                </div>
                                            }
                                            {
                                                loadingBusiness === true &&
                                                <div className="h-9 w-9 flex items-center justify-center bg-red rounded-[8px]">
                                                    <Spinner color="#FFFFFF" />
                                                </div>
                                            }
                                        </>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
            }
        </div >
    )
}

export default Profile;